import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  TextInput,
  ScrollView,
  Dimensions,
  Platform,
} from 'react-native';
import { MapView, MapMarker } from '../components/Map';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useIconTheme } from '../contexts/IconThemeContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useMapProvider } from '../contexts/MapProviderContext';
import { getColors } from '../styles/theme';
import {
  listHotspotsAdmin,
  getHotspotsInRegion,
  createImportBatch,
  bulkCreateHotspotsWithDedupe,
  updateHotspotAdmin,
  bulkUpdateHotspots,
} from '../services/database';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// OpenAI API key must be set via environment variables only (never commit keys).
// Use REACT_APP_OPENAI_API_KEY (web) or EXPO_PUBLIC_OPENAI_API_KEY (Expo) in .env
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY ||
  process.env.EXPO_PUBLIC_OPENAI_API_KEY;

const VALID_CATEGORIES = ['PARK', 'CAFE', 'BAR', 'CLUB', 'UNIVERSITY', 'PUBLIC_SQUARE', 'OTHER'];

// Real AI Service using OpenAI GPT
const generateAISuggestions = async (region, cityName, maxSuggestions = 20) => {
  console.log('🤖 Generating AI suggestions for:', cityName, region);
  
  // Calculate region bounds for the prompt
  let boundsDescription;
  let centerLat, centerLng;
  
  if (region.type === 'bounds') {
    centerLat = (region.minLat + region.maxLat) / 2;
    centerLng = (region.minLng + region.maxLng) / 2;
    boundsDescription = `within the rectangular area bounded by coordinates: 
      Southwest corner: (${region.minLat.toFixed(4)}, ${region.minLng.toFixed(4)})
      Northeast corner: (${region.maxLat.toFixed(4)}, ${region.maxLng.toFixed(4)})`;
  } else {
    centerLat = region.centerLat;
    centerLng = region.centerLng;
    boundsDescription = `within ${region.radiusKm}km of the center point at coordinates (${centerLat.toFixed(4)}, ${centerLng.toFixed(4)})`;
  }

  // Check if OpenAI API key is configured
  console.log('🔑 OpenAI API Key present:', OPENAI_API_KEY ? `Yes (${OPENAI_API_KEY.substring(0, 10)}...)` : 'No');
  
  if (!OPENAI_API_KEY) {
    console.warn('⚠️ OpenAI API key not configured. Using mock data.');
    console.log('To enable real AI suggestions, add REACT_APP_OPENAI_API_KEY or EXPO_PUBLIC_OPENAI_API_KEY to your .env file');
    
    // Return mock suggestions for testing when no API key
    return generateMockSuggestions(centerLat, centerLng, cityName, maxSuggestions);
  }
  
  console.log('🚀 Calling OpenAI API to generate suggestions...');

  const systemPrompt = `You are an expert on chess venues and social gathering spots around the world. 
Your task is to suggest real, existing locations where people play chess in ${cityName}.

IMPORTANT RULES:
1. Only suggest REAL places that actually exist - no fictional or made-up locations
2. Coordinates must be accurate and within the specified region
3. Category MUST be one of: PARK, CAFE, BAR, CLUB, UNIVERSITY, PUBLIC_SQUARE, OTHER
4. Each suggestion should be a place where chess is known to be played or would be suitable for chess

Return ONLY a valid JSON array with no additional text.`;

  const userPrompt = `Please suggest up to ${maxSuggestions} chess hotspots in ${cityName} ${boundsDescription}.

For each location, provide:
- name: The actual name of the venue
- category: One of PARK, CAFE, BAR, CLUB, UNIVERSITY, PUBLIC_SQUARE, OTHER
- description: A brief description (1-2 sentences) about why this is good for chess
- address: The actual street address
- latitude: Precise latitude coordinate (must be within the specified bounds)
- longitude: Precise longitude coordinate (must be within the specified bounds)
- schedule: Typical operating hours or best times to play (optional)
- activityLevel: HIGH, MEDIUM, or LOW based on how popular it is for chess (optional)
- amenities: Array of amenities like "Chess Tables", "WiFi", "Coffee", etc. (optional)

Respond with ONLY a JSON array, no markdown code blocks, no explanation.`;

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini', // Cost-effective model, change to 'gpt-4o' for better accuracy
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: 0.7,
        max_tokens: 4000,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('OpenAI API error:', response.status, errorData);
      throw new Error(`OpenAI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log('📩 OpenAI response received');
    const content = data.choices?.[0]?.message?.content;
    
    if (!content) {
      throw new Error('Empty response from OpenAI');
    }

    // Parse the JSON response - handle potential markdown code blocks
    let jsonContent = content.trim();
    if (jsonContent.startsWith('```')) {
      jsonContent = jsonContent.replace(/```json?\n?/g, '').replace(/```$/g, '').trim();
    }
    
    console.log('📝 Parsing OpenAI suggestions...');
    const suggestions = JSON.parse(jsonContent);
    
    if (!Array.isArray(suggestions)) {
      throw new Error('Response is not an array');
    }
    
    console.log(`✅ OpenAI returned ${suggestions.length} suggestions:`, suggestions.map(s => s.name));

    // Validate and filter suggestions
    const validatedSuggestions = suggestions
      .filter(s => {
        // Must have required fields
        if (!s.name || !s.category || !s.description || !s.address || 
            typeof s.latitude !== 'number' || typeof s.longitude !== 'number') {
          console.warn('Skipping invalid suggestion (missing fields):', s.name);
          return false;
        }
        
        // Category must be valid
        if (!VALID_CATEGORIES.includes(s.category.toUpperCase())) {
          console.warn('Skipping suggestion with invalid category:', s.name, s.category);
          return false;
        }
        
        // Coordinates must be within region
        if (region.type === 'bounds') {
          const inBounds = s.latitude >= region.minLat && s.latitude <= region.maxLat &&
                          s.longitude >= region.minLng && s.longitude <= region.maxLng;
          if (!inBounds) {
            console.warn('Skipping suggestion outside bounds:', s.name);
            return false;
          }
        }
        
        return true;
      })
      .map(s => ({
        ...s,
        category: s.category.toUpperCase(), // Normalize category
      }));

    console.log(`✅ Generated ${validatedSuggestions.length} valid suggestions from OpenAI`);
    return validatedSuggestions;

  } catch (error) {
    console.error('Error calling OpenAI:', error);
    
    // If OpenAI fails (quota, network, etc.), fall back to mock data for testing
    if (error.message?.includes('429') || error.message?.includes('quota')) {
      console.warn('⚠️ OpenAI quota exceeded. Falling back to mock data for testing.');
      console.log('💡 Add billing at https://platform.openai.com/account/billing to enable real AI suggestions');
      return generateMockSuggestions(centerLat, centerLng, cityName, maxSuggestions);
    }
    
    throw new Error(`AI suggestion failed: ${error.message}`);
  }
};

// Fallback mock suggestions when no API key or quota exceeded
const generateMockSuggestions = (centerLat, centerLng, cityName, maxSuggestions) => {
  // Use timestamp to generate unique names each time
  const timestamp = Date.now().toString().slice(-4);
  const uniqueId = Math.random().toString(36).substring(2, 6).toUpperCase();
  
  // Spread locations more widely (0.02 = ~2km spread) to avoid duplicates
  const spread = 0.02;
  
  const mockData = [
    {
      name: `Riverside Chess Pavilion ${uniqueId}`,
      category: 'PARK',
      description: `Popular outdoor chess spot in ${cityName} with permanent chess tables. Great for casual games with scenic river views.`,
      address: `${100 + parseInt(timestamp.slice(0,2))} Riverside Dr, ${cityName}`,
      latitude: centerLat + (Math.random() * spread) - (spread / 2),
      longitude: centerLng + (Math.random() * spread) - (spread / 2),
      schedule: 'Daily 8am - sunset',
      activityLevel: 'HIGH',
      amenities: ['Outdoor Chess Tables', 'Benches', 'Shade'],
    },
    {
      name: `Knight's Move Coffee House ${uniqueId}`,
      category: 'CAFE',
      description: `Cozy cafe in ${cityName} with chess boards available for patrons. Popular with local chess enthusiasts.`,
      address: `${200 + parseInt(timestamp.slice(1,3))} Queen St W, ${cityName}`,
      latitude: centerLat + (Math.random() * spread) - (spread / 2),
      longitude: centerLng + (Math.random() * spread) - (spread / 2),
      schedule: 'Mon-Sat 7am - 10pm',
      activityLevel: 'MEDIUM',
      amenities: ['WiFi', 'Coffee', 'Chess Sets Provided'],
    },
    {
      name: `${cityName} Grandmasters Club ${uniqueId}`,
      category: 'CLUB',
      description: `Local chess club welcoming players of all levels. Regular tournaments and casual play sessions.`,
      address: `${300 + parseInt(timestamp.slice(2,4))} King St E, ${cityName}`,
      latitude: centerLat + (Math.random() * spread) - (spread / 2),
      longitude: centerLng + (Math.random() * spread) - (spread / 2),
      schedule: 'Wed-Sun 2pm - 10pm',
      activityLevel: 'HIGH',
      amenities: ['Tournament Equipment', 'Chess Clocks', 'Coaching'],
    },
    {
      name: `Metro University Chess Society ${uniqueId}`,
      category: 'UNIVERSITY',
      description: `Student-run chess club in ${cityName}. Open to public visitors on weekends.`,
      address: `${400 + parseInt(timestamp.slice(0,2))} College St, ${cityName}`,
      latitude: centerLat + (Math.random() * spread) - (spread / 2),
      longitude: centerLng + (Math.random() * spread) - (spread / 2),
      schedule: 'Sat-Sun 10am - 6pm',
      activityLevel: 'LOW',
      amenities: ['Quiet Environment', 'Study Area', 'WiFi'],
    },
    {
      name: `Nathan Phillips Chess Plaza ${uniqueId}`,
      category: 'PUBLIC_SQUARE',
      description: `Giant chess board in ${cityName}'s public square. Popular meeting spot for players of all ages.`,
      address: `${500 + parseInt(timestamp.slice(1,3))} Bay St, ${cityName}`,
      latitude: centerLat + (Math.random() * spread) - (spread / 2),
      longitude: centerLng + (Math.random() * spread) - (spread / 2),
      schedule: 'Always open',
      activityLevel: 'MEDIUM',
      amenities: ['Giant Chess Board', 'Seating', 'Public Access'],
    },
  ];
  
  console.log(`📋 Generated ${mockData.length} mock suggestions with ID: ${uniqueId}`);
  return mockData.slice(0, maxSuggestions);
};

// Category definitions for reference
const CATEGORIES = {
  PARK: { label: 'Park', color: '#4CAF50' },
  CAFE: { label: 'Cafe', color: '#795548' },
  BAR: { label: 'Bar', color: '#FF5722' },
  CLUB: { label: 'Chess Club', color: '#9C27B0' },
  UNIVERSITY: { label: 'University', color: '#3F51B5' },
  PUBLIC_SQUARE: { label: 'Public Square', color: '#00BCD4' },
  LIBRARY: { label: 'Library', color: '#607D8B' },
  OTHER: { label: 'Other', color: '#9E9E9E' },
};

const AdminHotspotsMapScreen = ({ navigation }) => {
  const { getCategoryIcon } = useIconTheme();
  const { showToast } = useToast();
  const { appUser } = useAuth();
  const { isDarkMode } = useTheme();
  const { showStreetLabels } = useMapProvider();
  const colors = getColors(isDarkMode);
  const mapRef = useRef(null);

  // Data state
  const [hotspots, setHotspots] = useState([]);
  const [draftSuggestions, setDraftSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map region state
  const [region, setRegion] = useState({
    latitude: 43.6532, // Default to Toronto
    longitude: -79.3832,
    latitudeDelta: 0.05,
    longitudeDelta: 0.05,
  });

  // Region selection state
  const [selectionMode, setSelectionMode] = useState(null); // 'bounds' or 'radius'
  const [selectedBounds, setSelectedBounds] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedRadius, setSelectedRadius] = useState(2); // km

  // Modal states
  const [showAIModal, setShowAIModal] = useState(false);
  const [showHotspotDetail, setShowHotspotDetail] = useState(null);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  
  // AI suggestion state
  const [cityName, setCityName] = useState('');
  const [maxSuggestions, setMaxSuggestions] = useState('20');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  // Filter state
  const [showDraftsOnly, setShowDraftsOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);

  const isAdmin = appUser?.isAdmin === true;

  useEffect(() => {
    if (!isAdmin) {
      showToast('Access denied. Admin privileges required.', 'error');
      navigation.goBack();
      return;
    }
    fetchHotspots();
  }, [isAdmin]);

  const fetchHotspots = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      
      const fetchedHotspots = await listHotspotsAdmin(filters);
      setHotspots(fetchedHotspots);
    } catch (error) {
      console.error('Error fetching hotspots:', error);
      showToast('Failed to load hotspots', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (isAdmin) fetchHotspots();
  }, [statusFilter, isAdmin]);

  // Handle map press for region selection
  const handleMapPress = (event) => {
    if (!selectionMode) return;

    const { latitude, longitude } = event.nativeEvent?.coordinate || 
      { latitude: event.latLng?.lat(), longitude: event.latLng?.lng() };

    if (selectionMode === 'bounds') {
      if (!selectedBounds) {
        // First corner
        setSelectedBounds({ minLat: latitude, minLng: longitude });
        showToast('Tap again to set opposite corner', 'info');
      } else if (!selectedBounds.maxLat) {
        // Second corner
        setSelectedBounds({
          ...selectedBounds,
          maxLat: latitude,
          maxLng: longitude,
        });
        showToast('Region selected! Tap "AI Suggest" to continue', 'success');
      }
    } else if (selectionMode === 'radius') {
      setSelectedCenter({ latitude, longitude });
      showToast('Center selected! Adjust radius and tap "AI Suggest"', 'success');
    }
  };

  const clearSelection = () => {
    setSelectionMode(null);
    setSelectedBounds(null);
    setSelectedCenter(null);
  };

  const getSelectionRegion = () => {
    if (selectedBounds && selectedBounds.maxLat) {
      return {
        type: 'bounds',
        minLat: Math.min(selectedBounds.minLat, selectedBounds.maxLat),
        maxLat: Math.max(selectedBounds.minLat, selectedBounds.maxLat),
        minLng: Math.min(selectedBounds.minLng, selectedBounds.maxLng),
        maxLng: Math.max(selectedBounds.minLng, selectedBounds.maxLng),
      };
    }
    if (selectedCenter) {
      return {
        type: 'radius',
        centerLat: selectedCenter.latitude,
        centerLng: selectedCenter.longitude,
        radiusKm: selectedRadius,
      };
    }
    return null;
  };

  const handleStartAISuggest = () => {
    const selectionRegion = getSelectionRegion();
    if (!selectionRegion) {
      showToast('Please select a region first', 'warning');
      return;
    }
    setShowAIModal(true);
  };

  const handleGenerateSuggestions = async () => {
    if (!cityName.trim()) {
      showToast('Please enter a city name', 'warning');
      return;
    }

    const selectionRegion = getSelectionRegion();
    if (!selectionRegion) {
      showToast('No region selected', 'error');
      return;
    }

    setAiLoading(true);
    setShowAIModal(false);

    try {
      console.log('🚀 Starting AI suggestion generation...');
      console.log('Region:', selectionRegion);
      console.log('City:', cityName);

      // Step 1: Generate AI suggestions first (before creating batch)
      console.log('Step 1: Generating AI suggestions...');
      const suggestions = await generateAISuggestions(
        selectionRegion,
        cityName,
        parseInt(maxSuggestions) || 20
      );
      console.log(`✅ Generated ${suggestions.length} suggestions`);

      if (suggestions.length === 0) {
        showToast('No suggestions generated. Try a different region.', 'warning');
        setAiLoading(false);
        return;
      }

      // Step 2: Validate suggestions are within region
      console.log('Step 2: Validating suggestions within region...');
      const validatedSuggestions = suggestions.filter(s => {
        if (selectionRegion.type === 'bounds') {
          return (
            s.latitude >= selectionRegion.minLat &&
            s.latitude <= selectionRegion.maxLat &&
            s.longitude >= selectionRegion.minLng &&
            s.longitude <= selectionRegion.maxLng
          );
        }
        // For radius, calculate distance
        const distance = calculateDistance(
          selectionRegion.centerLat,
          selectionRegion.centerLng,
          s.latitude,
          s.longitude
        );
        return distance <= selectionRegion.radiusKm;
      });
      console.log(`✅ ${validatedSuggestions.length} suggestions passed validation`);

      // Step 3: Try to create import batch (may fail if table doesn't exist)
      let batchId = null;
      try {
        console.log('Step 3: Creating import batch...');
        const batch = await createImportBatch({
          created_by: appUser.id,
          region: selectionRegion,
          source: 'AI',
          city_name: cityName,
          notes: `AI suggestions for ${cityName}`,
        });
        batchId = batch.id;
        console.log('✅ Import batch created:', batchId);
      } catch (batchError) {
        console.warn('⚠️ Could not create import batch (table may not exist):', batchError.message);
        // Continue without batch - we can still create hotspots
      }

      // Step 4: Create hotspots with dedupe check - ALWAYS AS DRAFT
      console.log('Step 4: Creating hotspots with dedupe...');
      const result = await bulkCreateHotspotsWithDedupe(validatedSuggestions, {
        batchId: batchId,
        source: 'AI',
        createdBy: appUser.id,
        status: 'DRAFT', // CRITICAL: AI suggestions are ALWAYS drafts
        confidence: 'RELATED',
      });
      console.log('✅ Hotspots created:', result);

      setAiResults({
        batchId: batchId,
        ...result,
      });
      setShowSuggestionsModal(true);

      // Refresh hotspots to show new drafts
      fetchHotspots();

      showToast(
        `Created ${result.totalCreated} drafts, ${result.totalDuplicates} duplicates skipped`,
        result.totalCreated > 0 ? 'success' : 'info'
      );

    } catch (error) {
      console.error('❌ Error generating suggestions:', error);
      const errorMessage = error.message || 'Unknown error';
      showToast(`Failed: ${errorMessage}`, 'error');
      
      // Show error in results modal for visibility
      setAiResults({
        batchId: null,
        totalCreated: 0,
        totalDuplicates: 0,
        error: errorMessage,
      });
      setShowSuggestionsModal(true);
    } finally {
      setAiLoading(false);
    }
  };

  // Simple distance calculation
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleHotspotPress = (hotspot) => {
    setShowHotspotDetail(hotspot);
  };

  const handlePublishHotspot = async (hotspot) => {
    try {
      await updateHotspotAdmin(hotspot.id, {
        status: 'PUBLISHED',
        last_verified_at: new Date().toISOString(),
      });
      showToast('Hotspot published!', 'success');
      setShowHotspotDetail(null);
      fetchHotspots();
    } catch (error) {
      console.error('Error publishing hotspot:', error);
      showToast('Failed to publish hotspot', 'error');
    }
  };

  const handleArchiveHotspot = async (hotspot) => {
    try {
      await updateHotspotAdmin(hotspot.id, { status: 'ARCHIVED' });
      showToast('Hotspot archived', 'success');
      setShowHotspotDetail(null);
      fetchHotspots();
    } catch (error) {
      console.error('Error archiving hotspot:', error);
      showToast('Failed to archive hotspot', 'error');
    }
  };

  const handleDeleteHotspot = async (hotspot) => {
    try {
      await bulkUpdateHotspots([hotspot.id], 'DELETE', 'DELETE');
      showToast('Hotspot deleted', 'success');
      setShowHotspotDetail(null);
      fetchHotspots();
    } catch (error) {
      console.error('Error deleting hotspot:', error);
      showToast('Failed to delete hotspot', 'error');
    }
  };

  const handleUpdateConfidence = async (hotspot, confidence) => {
    try {
      await updateHotspotAdmin(hotspot.id, { confidence });
      showToast(`Confidence set to ${confidence}`, 'success');
      setShowHotspotDetail({ ...hotspot, confidence });
      fetchHotspots();
    } catch (error) {
      console.error('Error updating confidence:', error);
      showToast('Failed to update confidence', 'error');
    }
  };

  const getMarkerStyle = (hotspot) => {
    const isDraft = hotspot.status === 'DRAFT';
    const isArchived = hotspot.status === 'ARCHIVED';
    
    return {
      opacity: isDraft ? 0.6 : isArchived ? 0.3 : 1,
      scale: isDraft ? 0.8 : 1,
    };
  };

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.accessDenied}>
          <Icon name="lock" size={64} color="#ccc" />
          <Text style={styles.accessDeniedText}>Admin Access Required</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Admin Map</Text>
        <TouchableOpacity
          style={styles.headerButton}
          onPress={() => navigation.navigate('AdminHotspotsList')}
        >
          <Icon name="list" size={24} color="#2196F3" />
        </TouchableOpacity>
      </View>

      {/* Map */}
      <View style={styles.mapContainer}>
        <MapView
          mapRef={mapRef}
          style={styles.map}
          initialRegion={region}
          onPress={handleMapPress}
          isDarkMode={isDarkMode}
          showStreetLabels={showStreetLabels}
        >
          {/* Existing hotspots */}
          {hotspots.map((hotspot) => {
            if (!hotspot.latitude || !hotspot.longitude) return null;
            const markerStyle = getMarkerStyle(hotspot);
            
            return (
              <MapMarker
                key={hotspot.id}
                coordinate={{ latitude: hotspot.latitude, longitude: hotspot.longitude }}
                title={hotspot.name}
                description={`${hotspot.status} • ${hotspot.category}`}
                onPress={() => handleHotspotPress(hotspot)}
                customMarker={hotspot}
                isHotspot={true}
                hotspotScale={markerStyle.scale}
                style={{ opacity: markerStyle.opacity }}
              />
            );
          })}

          {/* Selection overlay markers */}
          {selectedBounds && (
            <MapMarker
              coordinate={{ latitude: selectedBounds.minLat, longitude: selectedBounds.minLng }}
              pinColor="#2196F3"
              title="Selection Start"
            />
          )}
          {selectedBounds?.maxLat && (
            <MapMarker
              coordinate={{ latitude: selectedBounds.maxLat, longitude: selectedBounds.maxLng }}
              pinColor="#2196F3"
              title="Selection End"
            />
          )}
          {selectedCenter && (
            <MapMarker
              coordinate={selectedCenter}
              pinColor="#FF9800"
              title={`Center (${selectedRadius}km radius)`}
            />
          )}
        </MapView>

        {/* Status Filter Chips */}
        <View style={styles.filterBar}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterScroll}>
            <TouchableOpacity
              style={[styles.filterChip, !statusFilter && styles.filterChipActive]}
              onPress={() => setStatusFilter(null)}
            >
              <Text style={[styles.filterChipText, !statusFilter && styles.filterChipTextActive]}>
                All ({hotspots.length})
              </Text>
            </TouchableOpacity>
            {['DRAFT', 'PUBLISHED', 'ARCHIVED'].map(status => {
              const count = hotspots.filter(h => h.status === status).length;
              return (
                <TouchableOpacity
                  key={status}
                  style={[styles.filterChip, statusFilter === status && styles.filterChipActive]}
                  onPress={() => setStatusFilter(status)}
                >
                  <View style={[styles.statusDot, { backgroundColor: getStatusColor(status) }]} />
                  <Text style={[styles.filterChipText, statusFilter === status && styles.filterChipTextActive]}>
                    {status} ({count})
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {/* Region Selection Controls */}
        <View style={styles.selectionControls}>
          {!selectionMode ? (
            <>
              <TouchableOpacity
                style={styles.selectionButton}
                onPress={() => setSelectionMode('bounds')}
              >
                <Icon name="crop-square" size={20} color="#fff" />
                <Text style={styles.selectionButtonText}>Draw Rectangle</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.selectionButton}
                onPress={() => setSelectionMode('radius')}
              >
                <Icon name="radio-button-unchecked" size={20} color="#fff" />
                <Text style={styles.selectionButtonText}>Center + Radius</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <View style={styles.selectionInfo}>
                <Icon name="info" size={16} color="#fff" />
                <Text style={styles.selectionInfoText}>
                  {selectionMode === 'bounds'
                    ? selectedBounds?.maxLat
                      ? 'Region selected!'
                      : selectedBounds
                        ? 'Tap opposite corner'
                        : 'Tap first corner'
                    : selectedCenter
                      ? 'Center selected!'
                      : 'Tap to set center'}
                </Text>
              </View>
              
              {selectionMode === 'radius' && selectedCenter && (
                <View style={styles.radiusControl}>
                  <Text style={styles.radiusLabel}>Radius: {selectedRadius} km</Text>
                  <View style={styles.radiusButtons}>
                    <TouchableOpacity
                      style={styles.radiusBtn}
                      onPress={() => setSelectedRadius(Math.max(0.5, selectedRadius - 0.5))}
                    >
                      <Icon name="remove" size={16} color="#fff" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.radiusBtn}
                      onPress={() => setSelectedRadius(Math.min(10, selectedRadius + 0.5))}
                    >
                      <Icon name="add" size={16} color="#fff" />
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              <TouchableOpacity
                style={styles.cancelSelectionButton}
                onPress={clearSelection}
              >
                <Icon name="close" size={18} color="#fff" />
              </TouchableOpacity>
            </>
          )}
        </View>

        {/* AI Suggest Button */}
        {getSelectionRegion() && (
          <TouchableOpacity
            style={styles.aiSuggestButton}
            onPress={handleStartAISuggest}
          >
            <Icon name="auto-awesome" size={24} color="#fff" />
            <Text style={styles.aiSuggestButtonText}>AI Suggest (Drafts)</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Loading Overlay */}
      {(loading || aiLoading) && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingBox}>
            <LoadingAnimation size={100} />
            <Text style={styles.loadingText}>
              {aiLoading ? 'Generating AI suggestions...' : 'Loading hotspots...'}
            </Text>
          </View>
        </View>
      )}

      {/* AI Suggestion Modal */}
      {showAIModal && (
        <View style={styles.modalOverlay}>
          <TouchableOpacity 
            style={styles.modalBackdrop} 
            activeOpacity={1} 
            onPress={() => setShowAIModal(false)}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Icon name="auto-awesome" size={32} color="#9C27B0" />
              <Text style={styles.modalTitle}>AI Hotspot Suggestions</Text>
            </View>
            
            <Text style={styles.modalSubtitle}>
              AI will suggest chess hotspots in the selected region.
              All suggestions will be saved as <Text style={styles.draftHighlight}>DRAFTS</Text> for your review.
            </Text>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>City Name *</Text>
              <TextInput
                style={styles.input}
                value={cityName}
                onChangeText={setCityName}
                placeholder="e.g. Toronto, New York..."
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Max Suggestions</Text>
              <TextInput
                style={styles.input}
                value={maxSuggestions}
                onChangeText={setMaxSuggestions}
                placeholder="20"
                keyboardType="numeric"
              />
            </View>

            <View style={styles.warningBox}>
              <Icon name="warning" size={20} color="#FF9800" />
              <Text style={styles.warningText}>
                AI suggestions are never auto-published. You must review and manually publish each hotspot.
              </Text>
            </View>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowAIModal(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.generateButton}
                onPress={handleGenerateSuggestions}
              >
                <Icon name="auto-awesome" size={18} color="#fff" />
                <Text style={styles.generateButtonText}>Generate Drafts</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* AI Results Modal */}
      {showSuggestionsModal && aiResults !== null && !aiLoading && (
        <View style={styles.modalOverlay}>
          <TouchableOpacity 
            style={styles.modalBackdrop} 
            activeOpacity={1} 
            onPress={() => {
              setShowSuggestionsModal(false);
              setAiResults(null);
            }}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Icon 
                name={aiResults.error ? "error" : "check-circle"} 
                size={32} 
                color={aiResults.error ? "#f44336" : "#4CAF50"} 
              />
              <Text style={styles.modalTitle}>
                {aiResults.error ? "AI Suggestion Failed" : "AI Suggestions Complete"}
              </Text>
            </View>

            {aiResults.error ? (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>{aiResults.error}</Text>
                <Text style={styles.errorHint}>
                  {aiResults.error.includes('OpenAI') || aiResults.error.includes('API') 
                    ? 'Make sure REACT_APP_OPENAI_API_KEY is set in your .env file'
                    : 'Check the console for more details'}
                </Text>
              </View>
            ) : (
              <View style={styles.resultsBox}>
                <View style={styles.resultRow}>
                  <Text style={styles.resultLabel}>Created as Drafts:</Text>
                  <Text style={styles.resultValue}>{aiResults.totalCreated || 0}</Text>
                </View>
                <View style={styles.resultRow}>
                  <Text style={styles.resultLabel}>Duplicates Skipped:</Text>
                  <Text style={styles.resultValue}>{aiResults.totalDuplicates || 0}</Text>
                </View>
                {aiResults.batchId && (
                  <View style={styles.resultRow}>
                    <Text style={styles.resultLabel}>Batch ID:</Text>
                    <Text style={styles.resultValueSmall}>{aiResults.batchId.substring(0, 8)}...</Text>
                  </View>
                )}
              </View>
            )}

            {!aiResults.error && aiResults.batchId && (
              <TouchableOpacity
                style={styles.viewBatchButton}
                onPress={() => {
                  setShowSuggestionsModal(false);
                  setAiResults(null);
                  navigation.navigate('ImportBatches', { batchId: aiResults?.batchId });
                }}
              >
                <Icon name="visibility" size={18} color="#fff" />
                <Text style={styles.viewBatchButtonText}>View in Batch Manager</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={styles.doneButton}
              onPress={() => {
                setShowSuggestionsModal(false);
                setAiResults(null);
                clearSelection();
                setCityName('');
              }}
            >
              <Text style={styles.doneButtonText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Hotspot Detail Sheet */}
      {showHotspotDetail !== null && (
        <View style={styles.detailOverlay}>
          <TouchableOpacity
            style={styles.detailBackdrop}
            activeOpacity={1}
            onPress={() => setShowHotspotDetail(null)}
          />
          <View style={styles.detailSheet}>
            {showHotspotDetail && (
              <>
                <View style={styles.detailHeader}>
                  <Text style={styles.detailIcon}>{getCategoryIcon(showHotspotDetail.category)}</Text>
                  <View style={styles.detailHeaderInfo}>
                    <Text style={styles.detailName}>{showHotspotDetail.name}</Text>
                    <View style={styles.detailBadges}>
                      <View style={[styles.statusBadge, { backgroundColor: getStatusColor(showHotspotDetail.status) }]}>
                        <Text style={styles.statusBadgeText}>{showHotspotDetail.status}</Text>
                      </View>
                      <Text style={styles.categoryLabel}>{CATEGORIES[showHotspotDetail.category]?.label}</Text>
                    </View>
                  </View>
                  <TouchableOpacity 
                    style={styles.closeDetailBtn}
                    onPress={() => setShowHotspotDetail(null)}
                  >
                    <Icon name="close" size={24} color="#666" />
                  </TouchableOpacity>
                </View>

                <Text style={styles.detailAddress}>📍 {showHotspotDetail.address}</Text>
                <Text style={styles.detailDescription}>{showHotspotDetail.description}</Text>

                {/* Confidence Selector */}
                <View style={styles.confidenceSection}>
                  <Text style={styles.sectionLabel}>Confidence Level:</Text>
                  <View style={styles.confidenceButtons}>
                    {['RELATED', 'KNOWN_HOTSPOT', 'CONFIRMED'].map(conf => (
                      <TouchableOpacity
                        key={conf}
                        style={[
                          styles.confidenceBtn,
                          showHotspotDetail.confidence === conf && styles.confidenceBtnActive
                        ]}
                        onPress={() => handleUpdateConfidence(showHotspotDetail, conf)}
                      >
                        <Text style={[
                          styles.confidenceBtnText,
                          showHotspotDetail.confidence === conf && styles.confidenceBtnTextActive
                        ]}>
                          {conf.replace('_', ' ')}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                {/* Action Buttons */}
                <View style={styles.detailActions}>
                  {showHotspotDetail.status !== 'PUBLISHED' && (
                    <TouchableOpacity
                      style={[styles.actionBtn, styles.publishBtn]}
                      onPress={() => handlePublishHotspot(showHotspotDetail)}
                    >
                      <Icon name="visibility" size={18} color="#fff" />
                      <Text style={styles.actionBtnText}>Publish</Text>
                    </TouchableOpacity>
                  )}
                  {showHotspotDetail.status !== 'ARCHIVED' && (
                    <TouchableOpacity
                      style={[styles.actionBtn, styles.archiveBtn]}
                      onPress={() => handleArchiveHotspot(showHotspotDetail)}
                    >
                      <Icon name="archive" size={18} color="#fff" />
                      <Text style={styles.actionBtnText}>Archive</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={[styles.actionBtn, styles.editBtn]}
                    onPress={() => {
                      setShowHotspotDetail(null);
                      navigation.navigate('AddEditHotspot', { hotspot: showHotspotDetail });
                    }}
                  >
                    <Icon name="edit" size={18} color="#fff" />
                    <Text style={styles.actionBtnText}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionBtn, styles.deleteBtn]}
                    onPress={() => {
                      if (confirm('Delete this hotspot permanently?')) {
                        handleDeleteHotspot(showHotspotDetail);
                      }
                    }}
                  >
                    <Icon name="delete" size={18} color="#fff" />
                    <Text style={styles.actionBtnText}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      )}
    </SafeAreaView>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'PUBLISHED': return '#4CAF50';
    case 'DRAFT': return '#FF9800';
    case 'ARCHIVED': return '#9E9E9E';
    default: return '#666';
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  headerButton: {
    padding: 4,
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  map: {
    flex: 1,
  },
  filterBar: {
    position: 'absolute',
    top: 10,
    left: 10,
    right: 10,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 8,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  filterScroll: {
    paddingHorizontal: 8,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    marginHorizontal: 4,
  },
  filterChipActive: {
    backgroundColor: '#2196F3',
  },
  filterChipText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  filterChipTextActive: {
    color: '#fff',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  selectionControls: {
    position: 'absolute',
    bottom: 100,
    left: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  selectionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2196F3',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    gap: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 4,
  },
  selectionButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  selectionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(33, 150, 243, 0.9)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    gap: 6,
  },
  selectionInfoText: {
    color: '#fff',
    fontSize: 13,
  },
  radiusControl: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 152, 0, 0.9)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    gap: 8,
  },
  radiusLabel: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  radiusButtons: {
    flexDirection: 'row',
    gap: 4,
  },
  radiusBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelSelectionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f44336',
    justifyContent: 'center',
    alignItems: 'center',
  },
  aiSuggestButton: {
    position: 'absolute',
    bottom: 30,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#9C27B0',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 25,
    gap: 10,
    shadowColor: '#9C27B0',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 8,
  },
  aiSuggestButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingBox: {
    backgroundColor: 'white',
    padding: 30,
    borderRadius: 16,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  accessDenied: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  accessDeniedText: {
    fontSize: 18,
    color: '#999',
  },
  modalOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 450,
    zIndex: 1001,
    ...Platform.select({
      web: {
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 20,
      },
    }),
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 8,
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  draftHighlight: {
    color: '#FF9800',
    fontWeight: 'bold',
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  warningBox: {
    flexDirection: 'row',
    backgroundColor: '#FFF3E0',
    padding: 12,
    borderRadius: 8,
    gap: 10,
    marginBottom: 20,
    alignItems: 'flex-start',
  },
  warningText: {
    flex: 1,
    fontSize: 13,
    color: '#E65100',
    lineHeight: 18,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  generateButton: {
    flex: 1,
    flexDirection: 'row',
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#9C27B0',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  generateButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  resultsBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  errorBox: {
    backgroundColor: '#FFEBEE',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: '#f44336',
  },
  errorText: {
    fontSize: 14,
    color: '#c62828',
    marginBottom: 8,
  },
  errorHint: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
  },
  resultRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },
  resultLabel: {
    fontSize: 14,
    color: '#666',
  },
  resultValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  resultValueSmall: {
    fontSize: 12,
    color: '#999',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  viewBatchButton: {
    flexDirection: 'row',
    backgroundColor: '#2196F3',
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
  },
  viewBatchButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  doneButton: {
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
  },
  doneButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  detailOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  detailBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  detailSheet: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '70%',
    zIndex: 1001,
    ...Platform.select({
      web: {
        boxShadow: '0 -5px 30px rgba(0,0,0,0.2)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -5 },
        shadowOpacity: 0.2,
        shadowRadius: 15,
        elevation: 20,
      },
    }),
  },
  closeDetailBtn: {
    padding: 4,
    marginLeft: 8,
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  detailIcon: {
    fontSize: 48,
    marginRight: 16,
  },
  detailHeaderInfo: {
    flex: 1,
  },
  detailName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 6,
  },
  detailBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: 'white',
  },
  categoryLabel: {
    fontSize: 13,
    color: '#666',
  },
  detailAddress: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  detailDescription: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
    marginBottom: 16,
  },
  confidenceSection: {
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
  },
  confidenceButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  confidenceBtn: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
  },
  confidenceBtnActive: {
    backgroundColor: '#4CAF50',
  },
  confidenceBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#666',
  },
  confidenceBtnTextActive: {
    color: '#fff',
  },
  detailActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    gap: 6,
  },
  publishBtn: {
    backgroundColor: '#4CAF50',
  },
  archiveBtn: {
    backgroundColor: '#9E9E9E',
  },
  editBtn: {
    backgroundColor: '#2196F3',
  },
  deleteBtn: {
    backgroundColor: '#f44336',
  },
  actionBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
});

export default AdminHotspotsMapScreen;

